import React, { useState } from 'react';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { truncateToWords, copyToClipboard } from '@/utils/common/textUtils';
import { CopyButton } from '@/components/ui/common/CopyButton';

interface QueuedVideoTileProps {
  readonly queuedVideo: QueuedVideo;
  readonly onCancelQueued?: (queuedId: string) => void;
  readonly handleCancel?: () => void;
}

// Render active state content (queued/processing)
const renderActiveContent = (
  status: 'queued' | 'processing',
  prompt: string,
  progress: number | undefined,
  copied: boolean,
  onCopy: () => void
) => {
  let statusText = 'Queued';
  if (status === 'processing') {
    statusText = progress && progress > 0 ? `${progress}%` : 'Processing';
  }
  
  return (
    <>
      <div className="text-xl font-semibold text-white mb-2">{statusText}</div>
      <div className="text-xs text-white mb-2 flex items-center justify-center gap-1">
        <span>{truncateToWords(prompt).text}</span>
        <CopyButton copied={copied} onClick={onCopy} />
      </div>
    </>
  );
};

// Render cancel button for queued and processing status
const renderCancelButton = (
  status: string,
  videoId: string,
  onCancelQueued?: (id: string) => void,
  handleCancel?: () => void
) => {
  // Show cancel button for both queued and processing states
  if ((status !== 'queued' && status !== 'processing') || (!onCancelQueued && !handleCancel)) return null;

  const handleCancelClick = () => {
    const message = 'Are you sure you want to cancel this video generation';
    
    if (globalThis.window.confirm(message)) {
      if (handleCancel) handleCancel();
      if (onCancelQueued) onCancelQueued(videoId);
    }
  };

  return (
    <button
      onClick={handleCancelClick}
      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors shadow-lg z-20 h-6 flex items-center justify-center border border-red-400"
      title="Cancel"
    >
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </button>
  );
};

/**
 * Individual queued video tile component
 * Used in virtualized grid to render queued/processing/failed video tiles
 */
const QueuedVideoTile: React.FC<QueuedVideoTileProps> = ({
  queuedVideo,
  onCancelQueued,
  handleCancel,
}) => {
  const [copied, setCopied] = useState(false);
  const isActiveState = queuedVideo.status === 'queued' || queuedVideo.status === 'processing';

  const handleCopyPrompt = () => {
    copyToClipboard(queuedVideo.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`rounded-xl overflow-hidden border-2 border-dashed relative ${
        isActiveState
          ? 'border-gray-400 tile-glow'
          : 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300'
      }`}
      style={{
        aspectRatio: '16/9',
        ...(isActiveState
          ? { background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)' }
          : {})
      }}
    >
      <div className="w-full h-full flex flex-col items-center justify-center p-4 relative">
        {isActiveState ? (
          <>
            {renderCancelButton(queuedVideo.status, queuedVideo.id, onCancelQueued, handleCancel)}

            {/* Simple centered progress display with organic shimmer effect */}
            <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden">
              {/* Multiple organic shimmer blobs */}
              <div className="absolute shimmer-morph morph-1" aria-hidden="true"></div>
              <div className="absolute shimmer-morph morph-2" aria-hidden="true"></div>
              <div className="absolute shimmer-morph morph-3" aria-hidden="true"></div>
              <div className="absolute shimmer-morph morph-4" aria-hidden="true"></div>
              <div className="absolute shimmer-morph morph-5" aria-hidden="true"></div>
              <div className="absolute shimmer-morph morph-6" aria-hidden="true"></div>

              {/* Content */}
              <div className="relative z-10 text-center">
                {renderActiveContent(
                  queuedVideo.status as 'queued' | 'processing',
                  queuedVideo.prompt,
                  queuedVideo.progress,
                  copied,
                  handleCopyPrompt
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Full UI for failed/other statuses */}
            <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
              {queuedVideo.status === 'failed' && (
                <>
                  <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Failed
                  </div>
                  {onCancelQueued && (
                    <button
                      onClick={() => onCancelQueued(queuedVideo.id)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors shadow-lg z-20 h-6 flex items-center justify-center border border-red-400"
                      title="Remove"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Content */}
            <div className="text-center">
              {queuedVideo.image && queuedVideo.image instanceof File && (
                <div className="mb-3">
                  <img
                    src={URL.createObjectURL(queuedVideo.image)}
                    alt="Reference"
                    className="w-16 h-16 object-cover rounded-lg mx-auto opacity-60"
                  />
                </div>
              )}
              <div className="mb-2">
                <svg className="w-8 h-8 text-blue-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-sm text-gray-700 font-medium mb-1">
                {queuedVideo.status === 'failed' && (queuedVideo.errorMessage || 'Veo 3 could not generate.')}
              </div>
              <div className="text-xs text-gray-600 mb-2 flex items-center justify-center gap-1">
                <span>{truncateToWords(queuedVideo.prompt).text}</span>
                <CopyButton copied={copied} onClick={handleCopyPrompt} colorClass="text-gray-600 hover:text-blue-500" />
              </div>
              <div className="text-xs text-gray-500">
                Model: {queuedVideo.model}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QueuedVideoTile;
